import React from "react";
import { useState, useRef, useEffect } from "react";
import {
    Box, Typography, Chip, Button, IconButton, Grid, TextField, Stack, Popper, Grow, Paper, ClickAwayListener, Tabs, Tab,
    Menu, MenuItem, Collapse
} from "@mui/material";
import moment from "moment";
import TableComponent from "components/grid";
import StatusBadge from "components/badge/StatusBadge";
import CustomPagination from "components/CustomPagination";
import { ReactComponent as EditIcon } from "assets/icons/EditIcon.svg";
import { LuEye, LuRefreshCw, LuSearch } from "react-icons/lu";
import { BiListUl } from "react-icons/bi";
import { MdOutlineFileDownload, MdOutlineFilterAlt, MdDeleteOutline } from "react-icons/md";
import { FaPlus } from "react-icons/fa6";
import Select from "react-select";
import CustomButton from "components/Button";
import EditABG from "./editFinancialBG/EditABG";
import ViewABG from "./viewFinancialBG/ViewABG";
// import DeleteLeads from "./DeleteLeads";
import { useLazyGetBankGuaranteeListQuery, useLazyGetExportBankGuaranteeListQuery } from "store/slices/apis/financialBGApiSlice";
import { useParams } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CalenderSVG from "assets/logo/Calendar";
import { useGetMasterDataQuery } from "store/slices/apis/masterConfigation";
import { mapAndSortAlphabetically } from "utils/mapSortHelper";
import { useDispatch } from "store";
import { DEFAULT_DATE_FORMAT } from "config";
import useExcelExport from "hooks/useExcelExport";
import { FINANCIAL_BG_EDIT, FINANCIAL_BG_VIEW } from "helpers/permissionConfig";
import { usePermission } from "hooks/usePermission";
import LegendStatusDefDialog from "./LegendStatusDefDialog";

const normalize = (value) =>
    (value || "")
        .toString()
        .trim()
        .toUpperCase();

const ABGTrackerList = () => {

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");

    const { projectId } = useParams();

    const editAccess = usePermission(FINANCIAL_BG_EDIT);
    const viewAccess = usePermission(FINANCIAL_BG_VIEW);
    
    const [openLegendStatusDefDialog, setOpenLegendStatusDefDialog] = useState(false);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleMenuOpen = (event, row) => {
        event.stopPropagation(); // prevent row click if any
        setMenuAnchorEl(event.currentTarget);
        setMenuRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    };

    const handleMenuView = () => {
        if (menuRow) {
            handleViewClick(menuRow);
        }
        handleMenuClose();
    };

    const handleMenuEdit = () => {
        if (menuRow) {
            handleEditFinancial(menuRow);
        }
        handleMenuClose();
    };

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortOrder, setSortOrder] = useState('desc');
    const [sortBy, setSortBy] = useState('CreatedDate');

    const [open, setOpen] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const anchorRef = useRef(null);
    const [isAddLeads, setIsAddLeads] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const [isView, setIsView] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [isDelete, setIsDelete] = useState(false);
    const [deleteData, setDeleteData] = useState(null);

    const [filters, setFilters] = useState({
        SortBy: sortBy,
        SortOrder: sortOrder,
        PageNumber: pageNumber,
        PageSize: pageSize,
        ProjectId: projectId,
        SearchTerm: "",
        GuaranteeType: "ABG",
    });

    const module = "FinancialBG";

    const { data: financialStatusRes } = useGetMasterDataQuery({ module: module, category: 'Status', ProjectId: projectId });
    const statusData = financialStatusRes?.data || [];
    const statusOptions = mapAndSortAlphabetically(statusData);

    const { data: issuingBankRes } = useGetMasterDataQuery({ module: module, category: 'IssuingBank', ProjectId: projectId });
    const issuingBankData = issuingBankRes?.data || [];
    const issuingBankOptions = mapAndSortAlphabetically(issuingBankData);

    const dispatch = useDispatch();
    const [trigger, { data: bankGuaranteeList, isFetching }] = useLazyGetBankGuaranteeListQuery();

    const { handleExport, isExporting } = useExcelExport({ exportHook: useLazyGetExportBankGuaranteeListQuery, fileName: "Financial_ABG_Tracker_Data" });

    const exportNCRLogData = (rows) =>
        rows.map((row, index) => ({
            "S.No": index + 1,
            "ID": row.customId || "-",
            "Contract / Project Name": row.contractProjectName || "-",
            "Beneficiary (Client / Owner)": row.beneficiary || "-",
            "Contract Value (₹ L)": row.contractValue || "-",
            "Issuing Bank": row.issuingBank || "-",
            "Branch / IFSC": row.branchOrIFSC || "-",
            "BG Reference Number": row.bgReferenceNumber || "-",
            "BG Amount (₹ L)": row.bgAmount || "-",
            "ABG % Of Contract": row.abgPercentageOfContract || "-",
            "Date Of Issue": row.dateOfIssue && moment(row.dateOfIssue).isValid()
                ? moment(row.dateOfIssue).format(DEFAULT_DATE_FORMAT)
                : '-',
            "Date Of Expiry": row.dateOfExpiry && moment(row.dateOfExpiry).isValid()
                ? moment(row.dateOfExpiry).format(DEFAULT_DATE_FORMAT)
                : '-',
            "Status": row.status || "-",
            "Date Of Release": row.dateOfRelease && moment(row.dateOfRelease).isValid()
                ? moment(row.dateOfRelease).format(DEFAULT_DATE_FORMAT)
                : '-',
            "Days To Expiry": row.daysToExpiry || "-",
            "Expiry Alert": row.expiryAlert || "-",
            "Validity (Days)": row.validityDays || "-",
            "Renewal Date": row.renewalDate && moment(row.renewalDate).isValid()
                ? moment(row.renewalDate).format(DEFAULT_DATE_FORMAT)
                : '-',
            "Claim Period (Days)": row.claimPeriodDays || "-",
            "Remarks / Conditions": row.remarksOrConditions || "-",
            "Last Updated": row.lastUpdated && moment(row.lastUpdated).isValid()
                ? moment(row.lastUpdated).format(DEFAULT_DATE_FORMAT)
                : '-',
        }));

    useEffect(() => {
        trigger(filters);
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm !== filters.SearchTerm) {
                const updatedFilters = {
                    ...filters,
                    SearchTerm: searchTerm,
                    PageNumber: 1,
                };
                setFilters(updatedFilters);
                trigger(updatedFilters);
                setPageNumber(1);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
        const updatedFilters = {
            ...filters,
            PageNumber: newPage,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPageNumber(1);
        const updatedFilters = {
            ...filters,
            PageSize: newSize,
            PageNumber: 1,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handleApplyFilters = () => {
        const updatedFilters = {
            ...filters,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
        handleClose();
    };

    const handleReset = () => {
        const resetFilters = {
            ProjectId: projectId,
            SortBy: sortBy,
            SortOrder: sortOrder,
            PageNumber: 1,
            PageSize: 10,
            SearchTerm: "",
            GuaranteeType: "ABG",
        };
        setFilters(resetFilters);
        trigger(resetFilters);
        setPageNumber(1);
        handleClose();
    };

    const handleSelectChange = (selectedOption, fieldName) => {
        const updatedFilters = {
            ...filters,
            [fieldName]: selectedOption?.value ?? null,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        const updatedFilters = {
            ...filters,
            [name]: value ? `${value}T00:00:00Z` : "",
            PageNumber: 1,
        };

        setFilters(updatedFilters);
    };

    const handleRequestSort = (property) => {
        const isAsc = sortBy === property && sortOrder === 'asc';
        const updatedFilters = {
            ...filters,
            SortBy: property,
            SortOrder: isAsc ? 'desc' : 'asc'
        };
        setFilters(updatedFilters);
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(property);
        trigger(updatedFilters);
    };

    const handleDelete = (filterType) => {
        const updatedFilters = {
            ...filters,
            [filterType]: "",
            GuaranteeType: "ABG",
            PageNumber: 1
        };

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
    };

    const handleAddLeads = () => {
        setIsAddLeads((prev) => !prev);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleEditFinancial = (row) => {
        setIsEdit((prev) => !prev);
        setSelectedData(row);
    };

    const handleEditClose = () => {
        setIsEdit((prev) => !prev);
        setSelectedData(null);
    };

    const handleViewClick = (row) => {
        setIsView((prev) => !prev);
        setViewData(row);
    };

    const handleViewClose = () => {
        setIsView(false);
        setViewData(null);
    };

    const handleDeleteClick = (row) => {
        setIsDelete((prev) => !prev);
        setDeleteData(row);
    };

    const handleDeleteClose = () => {
        setIsDelete(false);
        setDeleteData(null);
    };

    const handleMenuDelete = () => {
        if (menuRow) {
            handleDeleteClick(menuRow);
        }
        handleMenuClose();
    };

    const highlightText = (text, highlight) => {
        if (!highlight || !text) return text;

        const regex = new RegExp(`(${highlight})`, 'gi');
        const parts = String(text).split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <span
                    key={index}
                    style={{
                        backgroundColor: '#FFF3CD',
                        color: '#856404',
                        fontWeight: 'bold',
                        padding: '0 2px',
                        borderRadius: '3px'
                    }}
                >
                    {part}
                </span>
            ) : part
        );
    };

    const numericRenderer = (field) => (row) => {
        const value = row?.[field];

        if (!value && value !== 0) return "N/A";

        return Number(value).toLocaleString("en-IN");
    };

    const columns = [
        {
            name: "",
            field: "actions",
            width: "10px",
            sticky: true,
            renderer: (row) => (
                <>
                    {(viewAccess || editAccess) &&
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, row)}
                            >
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    }
                </>
            ),
        },
        {
            name: "ID",
            field: "customId",
            width: "100px",
            sticky: true,
        },
        {
            name: "Contract / Project Name",
            field: "contractProjectName",
            width: "250px",
            sticky: true,
            renderer: (row) => row?.contractProjectName ? highlightText(row.contractProjectName, searchTerm) : "N/A",
        },
        {
            name: "Beneficiary (Client / Owner)",
            field: "beneficiary",
            width: "250px",
            sticky: true,
            renderer: (row) => row?.beneficiary ? highlightText(row.beneficiary, searchTerm) : "N/A",
        },
        {
            name: "Contract Value (₹ L)",
            field: "contractValue",
            width: "200px",
            renderer: numericRenderer("contractValue"),
        },
        {
            name: "Issuing Bank",
            field: "issuingBank",
            width: "200px",
            renderer: (row) => row?.issuingBank ? highlightText(row.issuingBank, searchTerm) : "N/A",
        },
        {
            name: "Branch / IFSC",
            field: "branchOrIFSC",
            width: "200px",
            renderer: (row) => row?.branchOrIFSC ? row?.branchOrIFSC : "N/A",
        },
        {
            name: "BG Reference Number",
            field: "bgReferenceNumber",
            width: "200px",
            renderer: (row) => row?.bgReferenceNumber ? highlightText(row.bgReferenceNumber, searchTerm) : "N/A",
        },
        {
            name: "BG Amount (₹ L)",
            field: "bgAmount",
            width: "200px",
            renderer: numericRenderer("bgAmount"),
        },
        {
            name: "ABG % Of Contract",
            field: "abgPercentageOfContract",
            renderer: (row) => row?.abgPercentageOfContract ? row?.abgPercentageOfContract : "N/A",
        },
        {
            name: "Date Of Issue",
            field: "dateOfIssue",
            renderer: (row) => (
                row?.dateOfIssue ? moment.utc(row.dateOfIssue).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Date Of Expiry",
            field: "dateOfExpiry",
            renderer: (row) => (
                row?.dateOfExpiry ? moment.utc(row.dateOfExpiry).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Status",
            field: "status",
            renderer: (row) => (
                row?.status ? <StatusBadge status={row?.status} /> : "N/A"
            )
        },
        {
            name: "Date Of Release",
            field: "dateOfRelease",
            renderer: (row) => (
                row?.dateOfRelease ? moment.utc(row.dateOfRelease).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Days To Expiry",
            field: "daysToExpiry",
            renderer: (row) => row?.daysToExpiry ?? "N/A",
        },
        {
            name: "Expiry Alert",
            field: "expiryAlert",
            renderer: (row) => row?.expiryAlert ?? "N/A",
        },
        {
            name: "Validity (Days)",
            field: "validityDays",
            renderer: (row) => row?.validityDays ?? "N/A",
        },
        {
            name: "Renewal Date",
            field: "renewalDate",
            renderer: (row) => (
                row?.renewalDate ? moment.utc(row.renewalDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Claim Period (Days)",
            field: "claimPeriodDays",
            renderer: (row) => row?.claimPeriodDays ?? "N/A",
        },
        {
            name: "Remarks / Conditions",
            field: "remarksOrConditions",
            width: "300px",
            renderer: (row) => row?.remarksOrConditions ? highlightText(row.remarksOrConditions, searchTerm) : "N/A",
        },
        {
            name: "Last Updated",
            field: "lastUpdated",
            renderer: (row) => (
                row?.lastUpdated ? moment.utc(row.lastUpdated).format("DD MMM YYYY") : "N/A"
            ),
        },
    ];

    return (
        <>
            <Grid container spacing={2} sx={{ p: 2 }}>

                <Grid size={12}>
                    <Grid container>
                        <Grid size={{ md: 8, sm: 6 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                sx={{
                                    padding: "8px 0",
                                    alignItems: "center",
                                    minHeight: "40px"
                                }}
                            >

                                {filters.DateOfIssue && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Date Of Issue:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {moment(filters.DateOfIssue).format('DD-MM-YYYY')}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('DateOfIssue')}
                                    />
                                )}

                                {filters.DateOfExpiry && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Date Of Expiry:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {moment(filters.DateOfExpiry).format('DD-MM-YYYY')}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('DateOfExpiry')}
                                    />
                                )}

                                {filters.LastUpdated && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Last Updated Date:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {moment(filters.LastUpdated).format('DD-MM-YYYY')}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('LastUpdated')}
                                    />
                                )}

                                {filters.Status && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Status:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.Status}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('Status')}
                                    />
                                )}

                                {filters.IssuingBank && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Issuing Bank:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.IssuingBank}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('IssuingBank')}
                                    />
                                )}

                            </Stack>
                        </Grid>

                        <Grid size={{ md: 4, sm: 6 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { md: "row", xs: "column" },
                                    alignItems: { md: "center", xs: "flex-start" },
                                    justifyContent: 'end',
                                    p: "8px 0px"
                                }}
                            >
                                <TextField
                                    placeholder="Search By Project Name, Beneficiary, Issuing Bank ..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        startAdornment: <LuSearch style={{ marginRight: '8px', color: '#6B7280' }} />,
                                    }}
                                    sx={{
                                        minWidth: { md: '320px', xs: "auto" },
                                        // width: "100%",
                                        backgroundColor: '#fff',
                                        '& .MuiOutlinedInput-root': { borderRadius: '3px', minHeight: '36px', fontSize: '13px' },
                                        '& .MuiInputBase-input::placeholder': {
                                            fontSize: '0.9rem',
                                        }
                                    }}
                                    type="search"
                                />

                                <IconButton
                                    ref={anchorRef}
                                    onClick={handleToggle}
                                >
                                    <MdOutlineFilterAlt size={24} color="#48535B" />
                                </IconButton>

                                <Popper
                                    open={open}
                                    anchorEl={anchorRef.current}
                                    placement="bottom-end"
                                    transition
                                    disablePortal={false}
                                    sx={{ zIndex: 2000 }}
                                >
                                    {({ TransitionProps }) => (
                                        <Grow {...TransitionProps}>
                                            <Paper
                                                sx={{
                                                    mt: 1,
                                                    p: 2,
                                                    borderRadius: 1,
                                                    boxShadow: "0px 4px 20px rgba(7, 7, 7, 0.08)",
                                                    width: 600,
                                                }}
                                            >
                                                <ClickAwayListener onClickAway={handleClose}>
                                                    <Box>
                                                        <Grid container spacing={2}>
                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Status</Typography>
                                                                <Select
                                                                    name="Status"
                                                                    options={statusOptions}
                                                                    isClearable
                                                                    placeholder="Select Status"
                                                                    onChange={(option) => handleSelectChange(option, "Status")}
                                                                    value={statusOptions.find(option => option.value === filters.Status) || null}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Issuing Bank</Typography>
                                                                <Select
                                                                    name="IssuingBank"
                                                                    options={issuingBankOptions}
                                                                    isClearable
                                                                    placeholder="Select Issuing Bank"
                                                                    onChange={(option) => handleSelectChange(option, "IssuingBank")}
                                                                    value={issuingBankOptions.find(option => option.value === filters.IssuingBank) || null}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Date Of Issue</Typography>
                                                                <TextField
                                                                    type="date"
                                                                    name="DateOfIssue"
                                                                    // onBlur={handleBlur}
                                                                    value={filters.DateOfIssue ? new Date(filters.DateOfIssue).toISOString().split('T')[0] : ""}
                                                                    onChange={handleInputChange}
                                                                    onClick={(e) => {
                                                                        // Trigger the native date picker
                                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                        if (input) input.showPicker?.();
                                                                    }}
                                                                    fullWidth
                                                                    sx={{
                                                                        // ...rightTextStyle,
                                                                        "& input::-webkit-calendar-picker-indicator": {
                                                                            display: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& input[type='date']": {
                                                                            appearance: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                border: "none",
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                                            >
                                                                                {CalenderSVG}
                                                                            </Box>
                                                                        ),
                                                                        sx: {
                                                                            backgroundColor: "#E7E7EB",
                                                                            height: "40px",
                                                                            width: { xs: "100%", md: "100%" },
                                                                            borderRadius: "3px",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Date Of Expiry</Typography>
                                                                <TextField
                                                                    type="date"
                                                                    name="DateOfExpiry"
                                                                    // onBlur={handleBlur}
                                                                    value={filters.DateOfExpiry ? new Date(filters.DateOfExpiry).toISOString().split('T')[0] : ""}
                                                                    onChange={handleInputChange}
                                                                    onClick={(e) => {
                                                                        // Trigger the native date picker
                                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                        if (input) input.showPicker?.();
                                                                    }}
                                                                    fullWidth
                                                                    sx={{
                                                                        // ...rightTextStyle,
                                                                        "& input::-webkit-calendar-picker-indicator": {
                                                                            display: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& input[type='date']": {
                                                                            appearance: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                border: "none",
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                                            >
                                                                                {CalenderSVG}
                                                                            </Box>
                                                                        ),
                                                                        sx: {
                                                                            backgroundColor: "#E7E7EB",
                                                                            height: "40px",
                                                                            width: { xs: "100%", md: "100%" },
                                                                            borderRadius: "3px",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Last Updated Date</Typography>
                                                                <TextField
                                                                    type="date"
                                                                    name="LastUpdated"
                                                                    // onBlur={handleBlur}
                                                                    value={filters.LastUpdated ? new Date(filters.LastUpdated).toISOString().split('T')[0] : ""}
                                                                    onChange={handleInputChange}
                                                                    onClick={(e) => {
                                                                        // Trigger the native date picker
                                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                        if (input) input.showPicker?.();
                                                                    }}
                                                                    fullWidth
                                                                    sx={{
                                                                        // ...rightTextStyle,
                                                                        "& input::-webkit-calendar-picker-indicator": {
                                                                            display: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& input[type='date']": {
                                                                            appearance: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                border: "none",
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                                            >
                                                                                {CalenderSVG}
                                                                            </Box>
                                                                        ),
                                                                        sx: {
                                                                            backgroundColor: "#E7E7EB",
                                                                            height: "40px",
                                                                            width: { xs: "100%", md: "100%" },
                                                                            borderRadius: "3px",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid size={12}>
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        justifyContent: "flex-end",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    <Button onClick={handleReset}
                                                                        sx={{
                                                                            textTransform: "none",
                                                                            color: "#F15744",
                                                                            fontSize: "14px",
                                                                            lineHeight: "100%",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            borderRadius: "3px",
                                                                            fontWeight: 500,
                                                                            px: "20px",
                                                                            py: "15px",
                                                                        }}>
                                                                        Reset
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        onClick={handleApplyFilters}
                                                                        variant="contained"
                                                                        sx={{
                                                                            backgroundColor: "#00344C",
                                                                            borderRadius: "3px",
                                                                            textTransform: "none",
                                                                            fontWeight: 500,
                                                                            fontSize: "14px",
                                                                            lineHeight: "100%",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            color: "#FFFFFF",
                                                                            px: "20px",
                                                                            py: "15px",
                                                                        }}
                                                                    >
                                                                        Apply
                                                                    </Button>
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </ClickAwayListener>
                                            </Paper>
                                        </Grow>
                                    )}
                                </Popper>

                                <IconButton onClick={() =>
                                    handleExport(
                                        filters,
                                        exportNCRLogData
                                    )
                                } disabled={isExporting}>
                                    <MdOutlineFileDownload size={24} color="#48535B" />
                                </IconButton>

                                <IconButton onClick={() => {
                                    trigger(filters);
                                }}>
                                    <LuRefreshCw size={20} color="#48535B" />
                                </IconButton>

                            </Box>
                        </Grid>
                    </Grid>
                    <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => setOpenLegendStatusDefDialog(true)}
                            sx={{
                                textTransform: "none",
                                fontFamily: "Outfit",
                                fontSize: "13px",
                                color: "#0E73F6",
                            }}
                        >
                            Legend & Status Definitions
                        </Button>
                    </Box>
                </Grid>

                <Grid size={12}>
                    <TableComponent
                        data={bankGuaranteeList?.data || []}
                        isLoading={isFetching}
                        columns={columns}
                        order={sortOrder}
                        orderBy={sortBy}
                        createSortHandler={handleRequestSort}
                    />
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                mt: 1,
                                borderRadius: "3px",
                                minWidth: 140,
                                border: "1px solid #E5E9EB",
                                boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.12)",
                                overflow: "hidden",
                            },
                        }}
                        MenuListProps={{
                            sx: {
                                py: 0.5,
                            },
                        }}
                    >

                        {viewAccess && <MenuItem
                            onClick={handleMenuView}
                            sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontSize: "14px",
                                fontWeight: 400,
                                px: 1.5,
                                py: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                color: "#111827",
                                "&:hover": {
                                    backgroundColor: "#F6F8F9",
                                },
                            }}
                        >
                            <LuEye size={16} style={{ color: "#4B5563" }} />
                            <span>View</span>
                        </MenuItem>
                        }

                        {editAccess && <MenuItem
                            onClick={handleMenuEdit}
                            sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontSize: "14px",
                                fontWeight: 400,
                                px: 1.5,
                                py: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                color: "#111827",
                                "&:hover": {
                                    backgroundColor: "#F6F8F9",
                                },
                            }}
                        >
                            <EditIcon style={{ width: 14, height: 14 }} />
                            <span>Edit</span>
                        </MenuItem>
                        }
                        {/* <MenuItem
                            onClick={handleMenuDelete}
                            sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontSize: "14px",
                                fontWeight: 400,
                                px: 1.5,
                                py: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                color: "#111827",
                                "&:hover": {
                                    backgroundColor: "#F6F8F9",
                                },
                            }}
                        >
                            <MdDeleteOutline style={{ width: 14, height: 14 }} />
                            <span>Delete</span>
                        </MenuItem> */}

                    </Menu>

                    <CustomPagination
                        totalCount={bankGuaranteeList?.totalRecords || 0}
                        pageSize={pageSize}
                        page={pageNumber}
                        onPageChange={handlePageChange}
                        setCurrentPage={setPageNumber}
                        onPageSizeChange={handlePageSizeChange}
                    />

                </Grid>
            </Grid >
            {isEdit && <EditABG open={isEdit} handleClose={handleEditClose} rowData={selectedData} tabKey="ABG" statusOptions={statusOptions} issuingBankOptions={issuingBankOptions} />}
            {isView && <ViewABG open={isView} handleClose={handleViewClose} rowData={viewData} />}

            {/* 
            {isDelete && <DeleteLeads open={isDelete} handleClose={handleDeleteClose} rowData={deleteData} />} */}

            <LegendStatusDefDialog
                open={openLegendStatusDefDialog}
                onClose={() => setOpenLegendStatusDefDialog(false)}
            />
        </>
    );
}

export default ABGTrackerList;